import dedent from 'ts-dedent'
import {
  Provider,
  QueryAndOutputs,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType,
  WorkflowComponentParameterMode,
  ColumnDataType,
  GetOutputSchemaParams,
  TableSchema
} from '../types'
import { addColumns } from '../utils/schemas'
import { SingleOutputWorkflowComponent, SINGLE_OUTPUT_NAME } from './common/singleoutputcomponent'

class OracleRasterSummaryStats extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'oracle.rastersummarystats',
        group: 'Oracle',
        title: 'Raster Summary Statistics (Oracle)',
        description: dedent`
          Computes area-weighted statistics from raster values over polygon features.
          Creates an augmented table with all original vector columns plus new columns
          for each band's statistics. Supports single or multi-band rasters and CRS transformation.
        `,
        starred: true
      },
      [
        // Raster input
        {
          name: 'rasterTable',
          type: WorkflowComponentParamType.InputTable,
          title: 'Raster table',
          description: 'GeoRaster table containing single or multi-band raster data'
        },

        // Vector input
        {
          name: 'vectorTable',
          type: WorkflowComponentParamType.InputTable,
          title: 'Vector table',
          description: 'Table containing polygon features to intersect with raster'
        },
        {
          name: 'geomCol',
          type: WorkflowComponentParamType.Column,
          parent: 'vectorTable',
          title: 'Geometry column',
          dataType: [ColumnDataType.Geometry],
          default: 'GEOM'
        },

        // Band configuration
        {
          name: 'bands',
          type: WorkflowComponentParamType.String,
          title: 'Band indices',
          description: 'Comma-separated band indices (0-based). Leave empty to analyze all bands',
          placeholder: 'e.g., 0,1,2'
        },

        // Mode selection
        {
          name: 'mode',
          type: WorkflowComponentParamType.Selection,
          title: 'Configuration mode',
          options: ['simple', 'advanced'],
          default: 'simple',
          description: 'Simple mode for common statistics, advanced for custom expressions'
        },

        // Simple mode - checkboxes for common statistics
        {
          name: 'includeMean',
          type: WorkflowComponentParamType.Boolean,
          title: 'Mean value',
          default: true,
          showIf: [{ parameter: 'mode', value: 'simple' }]
        },
        {
          name: 'includeMin',
          type: WorkflowComponentParamType.Boolean,
          title: 'Minimum value',
          default: false,
          showIf: [{ parameter: 'mode', value: 'simple' }]
        },
        {
          name: 'includeMax',
          type: WorkflowComponentParamType.Boolean,
          title: 'Maximum value',
          default: false,
          showIf: [{ parameter: 'mode', value: 'simple' }]
        },
        {
          name: 'includeMedian',
          type: WorkflowComponentParamType.Boolean,
          title: 'Median value',
          default: false,
          showIf: [{ parameter: 'mode', value: 'simple' }]
        },
        {
          name: 'includeStdDev',
          type: WorkflowComponentParamType.Boolean,
          title: 'Standard deviation',
          default: false,
          showIf: [{ parameter: 'mode', value: 'simple' }]
        },
        {
          name: 'includeSum',
          type: WorkflowComponentParamType.Boolean,
          title: 'Sum of values',
          default: false,
          showIf: [{ parameter: 'mode', value: 'simple' }]
        },
        {
          name: 'includePixelCount',
          type: WorkflowComponentParamType.Boolean,
          title: 'Pixel count',
          default: true,
          showIf: [{ parameter: 'mode', value: 'simple' }]
        },

        // Advanced mode - custom SQL expressions
        {
          name: 'customExpressions',
          type: WorkflowComponentParamType.StringSql,
          mode: WorkflowComponentParameterMode.Multiline,
          title: 'Custom expressions',
          showIf: [{ parameter: 'mode', value: 'advanced' }],
          placeholder: dedent`
            -- Example: Calculate NDVI from bands 0 (RED) and 1 (NIR)
            (BAND1_MEAN_VAL - BAND0_MEAN_VAL) / NULLIF(BAND1_MEAN_VAL + BAND0_MEAN_VAL, 0) AS NDVI
          `,
          description: 'Additional calculated columns (e.g., vegetation indices)'
        },

        // Advanced options
        {
          name: 'pyramidLevel',
          type: WorkflowComponentParamType.Number,
          title: 'Pyramid level',
          default: 0,
          advanced: true,
          description: '0=full resolution, 1-5=pyramid levels (faster but less accurate)'
        },
        {
          name: 'valuePrefix',
          type: WorkflowComponentParamType.String,
          title: 'Column prefix',
          description: 'Prefix to add to generated column names (e.g., "NDVI_")',
          optional: true,
          advanced: true
        },
        {
          name: 'nonIntersectingRows',
          type: WorkflowComponentParamType.Selection,
          title: 'Non-intersecting rows',
          options: ['filter', 'null'],
          default: 'null',
          advanced: true,
          description: 'How to handle parcels outside raster extent: filter out or return NULL values'
        }
      ]
    )
  }

  async getCode({
    workflow,
    params,
    connection,
    componentName,
    codeType,
    schemas
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs> {
    const prepParams = this._prepareParameters(params)

    const rasterTable = connection.quoteName(prepParams.rasterTable as string)
    const vectorTable = connection.quoteName(prepParams.vectorTable as string)
    const outputTable = connection.getOutputTableName(workflow.id, componentName, params, SINGLE_OUTPUT_NAME, codeType)
    const geomCol = prepParams.geomCol as string
    const valuePrefix = prepParams.valuePrefix || ''
    const handleNonIntersecting = prepParams.nonIntersectingRows || 'null'

    // Get the schema of the vector table to explicitly list all columns
    const vectorSchema = schemas?.input['vectorTable'] as TableSchema
    const vectorColumns = vectorSchema
      ? vectorSchema.map((col) => `  px.${connection.quoteName(col.name)}`).join(',\n')
      : '  px.*'

    // Parse bands parameter
    let bandsList: number[] = []
    if (prepParams.bands && (prepParams.bands as string).trim()) {
      bandsList = (prepParams.bands as string)
        .split(',')
        .map((b) => parseInt(b.trim()))
        .filter((b) => !isNaN(b))
    }

    // Build statistics list based on mode
    const statistics: string[] = []
    if (prepParams.mode === 'simple') {
      if (prepParams.includeMean) statistics.push('MEAN')
      if (prepParams.includeMin) statistics.push('MIN')
      if (prepParams.includeMax) statistics.push('MAX')
      if (prepParams.includeMedian) statistics.push('MEDIAN')
      if (prepParams.includeStdDev) statistics.push('STD')
      if (prepParams.includeSum) statistics.push('SUM')
      if (prepParams.includePixelCount) statistics.push('PIXEL_COUNT')
    }

    // Helper function to generate statistic columns for a band
    const generateStatColumns = (bandIndex: number, wrapWithNullCheck = false): string[] => {
      const columns: string[] = []
      const transformExpr = `CASE
        WHEN px.${geomCol}.SDO_SRID = rd.raster_srid OR px.${geomCol}.SDO_SRID IS NULL
        THEN px.${geomCol}
        ELSE SDO_CS.TRANSFORM(px.${geomCol}, rd.raster_srid)
      END`

      statistics.forEach((stat) => {
        if (stat === 'PIXEL_COUNT') {
          // Pixel count calculated as SUM/MEAN
          if (statistics.includes('SUM') && statistics.includes('MEAN')) {
            columns.push(
              `  ROUND(${valuePrefix}BAND${bandIndex}_SUM_VAL / NULLIF(${valuePrefix}BAND${bandIndex}_MEAN_VAL, 0)) AS ${valuePrefix}BAND${bandIndex}_PIXEL_COUNT`
            )
          } else {
            // Calculate inline if SUM/MEAN not already included
            const sumExpr = `SDO_GEOR.generateStatisticsSum(rd.geor, ${bandIndex}, 'samplingFactor=1', ${transformExpr}, NULL, 'FALSE', NULL)`
            const meanExpr = `SDO_GEOR.generateStatisticsMean(rd.geor, ${bandIndex}, 'samplingFactor=1', ${transformExpr}, NULL, 'FALSE', NULL)`
            if (wrapWithNullCheck) {
              columns.push(`  CASE
    WHEN SDO_ANYINTERACT(rd.geor.spatialExtent, ${transformExpr}) = 'TRUE'
    THEN ROUND(${sumExpr} / NULLIF(${meanExpr}, 0))
    ELSE NULL
  END AS ${valuePrefix}BAND${bandIndex}_PIXEL_COUNT`)
            } else {
              columns.push(`  ROUND(${sumExpr} / NULLIF(${meanExpr}, 0)) AS ${valuePrefix}BAND${bandIndex}_PIXEL_COUNT`)
            }
          }
        } else {
          const funcName = stat === 'STD' ? 'STD' : stat.charAt(0) + stat.slice(1).toLowerCase()
          const statExpr = `SDO_GEOR.generateStatistics${funcName}(rd.geor, ${bandIndex}, 'samplingFactor=1', ${transformExpr}, NULL, 'FALSE', NULL)`

          if (wrapWithNullCheck) {
            columns.push(`  CASE
    WHEN SDO_ANYINTERACT(rd.geor.spatialExtent, ${transformExpr}) = 'TRUE'
    THEN ${statExpr}
    ELSE NULL
  END AS ${valuePrefix}BAND${bandIndex}_${stat}_VAL`)
          } else {
            columns.push(`  ${statExpr} AS ${valuePrefix}BAND${bandIndex}_${stat}_VAL`)
          }
        }
      })

      return columns
    }

    const allStatColumns: string[] = []
    const useNullCheck = handleNonIntersecting === 'null'

    bandsList.forEach((band) => {
      allStatColumns.push(...generateStatColumns(band, useNullCheck))
    })

    // Add WHERE clause for filtering if needed
    const whereClause =
      handleNonIntersecting === 'filter'
        ? `WHERE SDO_ANYINTERACT(rd.geor.spatialExtent,
          CASE
            WHEN px.${geomCol}.SDO_SRID = rd.raster_srid OR px.${geomCol}.SDO_SRID IS NULL
            THEN px.${geomCol}
            ELSE SDO_CS.TRANSFORM(px.${geomCol}, rd.raster_srid)
          END) = 'TRUE'`
        : ''

    const select = dedent`
        WITH raster_data AS (
          SELECT
            r.RASTER AS geor,
            SDO_GEOR.getModelSRID(r.RASTER) AS raster_srid,
            SDO_GEOR.getBandDimSize(r.RASTER) AS band_count
          FROM ${rasterTable} r
          WHERE SDO_GEOR.getModelSRID(r.RASTER) IS NOT NULL
          ORDER BY r.ID DESC
          FETCH FIRST 1 ROWS ONLY
        )
        SELECT
        ${vectorColumns},
        ${allStatColumns.join(',\n')}
        ${prepParams.mode === 'advanced' && prepParams.customExpressions ? `,\n  ${prepParams.customExpressions}` : ''}
        FROM ${vectorTable} px
        CROSS JOIN raster_data rd
        ${whereClause}
      `
    const query = connection.getCreateTempTableQuery(outputTable, select, codeType, null)

    return {
      query,
      outputs: { [SINGLE_OUTPUT_NAME]: outputTable }
    }
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({ columns, params }: GetOutputSchemaParams): TableSchema {
    const prepParams = this._prepareParameters(params)
    const valuePrefix = (prepParams.valuePrefix as string) || ''

    // Start with the source table columns
    const sourceSchema = columns.vectorTable || []

    let bandsList: number[] = []
    if (prepParams.bands && (prepParams.bands as string).trim()) {
      bandsList = (prepParams.bands as string)
        .split(',')
        .map((b) => parseInt(b.trim()))
        .filter((b) => !isNaN(b))
    }

    // Build statistics list based on mode
    const statistics: string[] = []
    if (prepParams.mode === 'simple') {
      if (prepParams.includeMean) statistics.push('MEAN')
      if (prepParams.includeMin) statistics.push('MIN')
      if (prepParams.includeMax) statistics.push('MAX')
      if (prepParams.includeMedian) statistics.push('MEDIAN')
      if (prepParams.includeStdDev) statistics.push('STD')
      if (prepParams.includeSum) statistics.push('SUM')
      if (prepParams.includePixelCount) statistics.push('PIXEL_COUNT')
    }

    // Generate column definitions for each band and statistic
    const newColumns: Array<{ name: string; type: ColumnDataType }> = []

    if (bandsList.length > 0) {
      // We know exactly which bands to include
      for (const bandIndex of bandsList) {
        for (const stat of statistics) {
          if (stat === 'PIXEL_COUNT') {
            newColumns.push({
              name: `${valuePrefix}BAND${bandIndex}_PIXEL_COUNT`,
              type: ColumnDataType.Number
            })
          } else {
            newColumns.push({
              name: `${valuePrefix}BAND${bandIndex}_${stat}_VAL`,
              type: ColumnDataType.Number
            })
          }
        }
      }
    }

    return addColumns(sourceSchema, newColumns)
  }

  supportedProviders(): Provider[] {
    return [Provider.Oracle]
  }
}

export { OracleRasterSummaryStats }
